import { ApiRef, createApiRef } from '@backstage/frontend-plugin-api';

import type { EntitlementType as BillingAccountApiEntitlementType } from './billing-account-api-sdk';

export type BillingAccount = {
  billingID: string;
  organizationID: string;
  organizationProfile?: string | null;
  projectIDs: Array<string>;
  billingModel: string;
  billingProcessor: string;
  paymentMethod: string;
  pricingPlan: string;
  status: string;
  email: string;
  name: string;
  reason: string;
  isFunded: boolean;
  unfundedReason: string;
  productCatalogueVersion: string;
};

export type Transition = {
  timestamp: string;
  isFunded: boolean;
  unfundedReason: string;
};

export type CatalogueVersion = {
  version: string;
  validFrom: string; // RFC3339 timestamp as string
};

export type Override = {
  ID: string;
  billingAccountID: string;
  createdAt: string;
  createdBy: string;
  comment: string;
  state: string;
  deletedAt?: Date;
  deletedBy?: string;
  deletionReason?: string;
};

export type PaymentProcessorData = {
  stripeCustomerID?: string;
};

export type PaymentProcessor = {
  paymentProcessorID: string;
  type: string;
  state: string;
  data?: PaymentProcessorData;
};

export type Marketplace = {
  id: string;
  type: string;
  legacy: boolean;
  entitlementID?: string | null;
  externalAccountID?: string | null;
  externalReportingID?: string | null;
  isActive: boolean;
  inactiveReason?: string | null;
  deletedAt?: Date | null;
};

export type Entitlement = {
  id: string;
  amount: number;
  costBasis: string;
  status: string;
  type: string;
  start: string; // RFC3339 timestamp as string
  end: string; // RFC3339 timestamp as string
  claimedAt?: string; // RFC3339 timestamp as string, absent if not yet claimed
  orbCreditBlockID?: string;
  salesforceSubID?: string;
  rollOverCreditBlockID?: string;
  rolloverPercentage?: number;
  provisioningID?: string;
};

export type CreateEntitlementParams = {
  start: string; // RFC3339 timestamp as string
  end: string; // RFC3339 timestamp as string
  costBasis: string;
  amount: number;
  type: BillingAccountApiEntitlementType;
  rolloverPercentage?: number; // Optional rollover percentage for unused ACUs
  salesforceSubID?: string; // Optional Salesforce Subscription ID
  provisioningID?: string; // Optional provisioning ID
};

export type BillingAccountRecord = {
  billingAccount: BillingAccount;
  paymentProcessor?: PaymentProcessor;
  marketplace?: Marketplace;
  overrides: Override[];
};

export class BillingAccountError extends Error {
  readonly statusCode: number;

  constructor(message: string, statusCode: number) {
    const prefix = Number.isFinite(statusCode) ? `Status ${statusCode}: ` : '';
    super(`${prefix}${message}`);
    this.name = 'BillingAccountError';
    this.statusCode = statusCode;

    if (typeof (Error as any).captureStackTrace === 'function') {
      (Error as any).captureStackTrace(this, BillingAccountError);
    }
  }
}

export type OverrideState = 'funded' | 'unfunded';

export interface BillingAccountApi {
  getBillingAccountById(
    billingID: string,
  ): Promise<BillingAccountRecord | BillingAccountError>;
  getBillingAccountByProjectId(
    billingID: string,
  ): Promise<BillingAccountRecord | BillingAccountError>;
  getBillingAccountByMarketplaceID(
    marketplaceID: string,
  ): Promise<BillingAccountRecord | BillingAccountError>;
  getBillingAccountByOrgId(
    organizationID: string,
  ): Promise<BillingAccountRecord[] | BillingAccountError>;
  getBillingAccountByEntitlementID(
    entitlementID: string,
  ): Promise<BillingAccountRecord | BillingAccountError>;
  createOverride(
    billingID: string,
    createdBy: string,
    comment: string,
    state: OverrideState,
  ): Promise<string | BillingAccountError>;
  deleteOverride(
    billingID: string,
    overrideID: string,
    deletedBy: string,
    deletionReason: string,
  ): Promise<void | BillingAccountError>;
  reactivateMarketplace(
    marketplaceID: string,
  ): Promise<void | BillingAccountError>;
  getBillingAccountTransitions(
    billingID: string,
  ): Promise<Transition[] | BillingAccountError>;
  getProductCatalogueVersions(): Promise<
    CatalogueVersion[] | BillingAccountError
  >;
  setProductCatalogueVersion(
    billingID: string,
    productCatalogueVersion: string,
    reason: string,
  ): Promise<void | BillingAccountError>;
  getEntitlementsByBillingId(
    billingID: string,
  ): Promise<Entitlement[] | BillingAccountError>;
  getEntitlementById(
    entitlementID: string,
  ): Promise<Entitlement | BillingAccountError>;
  createEntitlement(
    params: CreateEntitlementParams,
  ): Promise<string | BillingAccountError>;
  updateEntitlement(
    entitlementID: string,
    params: CreateEntitlementParams,
  ): Promise<Entitlement | BillingAccountError>;
  claimEntitlement(
    entitlementID: string,
    billingAccountID: string,
  ): Promise<Entitlement | BillingAccountError>;
  voidEntitlement(
    entitlementID: string,
  ): Promise<Entitlement | BillingAccountError>;
  deleteEntitlement(entitlementID: string): Promise<void | BillingAccountError>;
}

export const billingAccountApiRef: ApiRef<BillingAccountApi> =
  createApiRef<BillingAccountApi>({
    id: 'plugin.billing-account-api.service',
  });
